import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout, Card, Button } from '../components';
import { ProjectsListTable, type ProjectsListColumn, type ProjectsListServerQuery } from '../components/ProjectsListTable';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { Breadcrumb } from '../components/Breadcrumb';
import { formatDateTime } from '../components/AdminInvitationsList';
import { adminApi, type AdminClientRow, type AdminListQuery } from '../services/api';
import { bankNamesText } from '../components/CustomerBanks';
import { downloadBlob } from '../components/RegistrationReport';

/**
 * A row in Alla beställare: a client record at a contractor, or (S8) a client
 * account without any contractor, which has no client record to open. The
 * server searches, filters, sorts and pages the list (ClickUp 869fjkyz1).
 */
type ClientRow = AdminClientRow;

const DEFAULT_COLUMN_ORDER = [
  'companyNumber', 'companyName', 'customerNumber', 'name', 'email',
  'phone', 'address', 'postalCode', 'city', 'banks', 'notes', 'createdAt', 'age', 'verifiedCustomer'
];

/** The stored status code values, in the order the status filter lists them. */
const STATUS_VALUES = ['Delad', 'Verifierad'] as const;

const calculateAgeDays = (dateStr: string): number => {
  if (!dateStr) return 0;
  const created = new Date(dateStr);
  const today = new Date();
  // Whole days: compare the dates only.
  const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((todayDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
};

/** Alla beställare, in the same list table as contractors (S8). */
export function AdminCustomerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState<ClientRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const lastQuery = useRef<AdminListQuery>({});
  const latestRequest = useRef(0);
  // Same saved preference key as before the shared table, so a saved order survives.
  const [columnOrder, handleColumnReorder] = useColumnOrder('adminCustomerListColumns', DEFAULT_COLUMN_ORDER);

  // Only the newest answer counts, so a slow earlier page cannot replace it.
  const loadPage = useCallback(async ({ page, pageSize, sort, filters }: ProjectsListServerQuery) => {
    const request = ++latestRequest.current;
    lastQuery.current = { sort: sort.field, direction: sort.direction, filters };
    setIsLoading(true);
    try {
      const result = await adminApi.getCustomerPage({ page, pageSize, sort: sort.field, direction: sort.direction, filters });
      if (request !== latestRequest.current) return;
      setCustomers(result.data);
      setTotal(result.total);
      setLoadError(false);
    } catch (error) {
      if (request !== latestRequest.current) return;
      console.error('Failed to load clients:', error);
      setLoadError(true);
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, []);

  const statusLabel = (value: string) =>
    value === 'Verifierad' ? t('customers.status.verified') : t('customers.status.shared');

  const statusBadge = (customer: ClientRow, className = '') => (
    <span className={`badge ${customer.verifiedCustomer === 'Verifierad' ? 'bg-success' : 'bg-warning text-dark'} ${className}`.trim()}>
      {statusLabel(customer.verifiedCustomer)}
    </span>
  );

  const statusOptions = useMemo(
    () => STATUS_VALUES.map(value => ({ value, label: statusLabel(value) })),
    // statusLabel only depends on the language (t).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const contractorLink = (customer: ClientRow) => {
    if (customer.standalone) return <span className="text-muted">{t('adminCustomerInvitations.noContractor')}</span>;
    if (!customer.companyName) return <span className="text-muted">—</span>;
    return customer.companyId ? (
      <Link to={`/admin/company-list/${customer.companyId}`} onClick={(e) => e.stopPropagation()}>{customer.companyName}</Link>
    ) : customer.companyName;
  };

  const columns: ProjectsListColumn<ClientRow>[] = useMemo(() => [
    { field: 'companyNumber', label: t('adminCustomers.columns.companyNumber'), sortValue: c => c.companyNumber ?? Number.MAX_SAFE_INTEGER, filterValue: c => c.companyNumber == null ? '' : String(c.companyNumber), render: c => c.companyNumber },
    { field: 'companyName', label: t('adminCustomers.columns.companyName'), sortValue: c => c.companyName ?? '', filterValue: c => c.companyName ?? (c.standalone ? t('adminCustomerInvitations.noContractor') : ''), render: c => contractorLink(c) },
    { field: 'customerNumber', label: t('customers.columns.customerNumber'), sortValue: c => c.customerNumber ?? Number.MAX_SAFE_INTEGER, filterValue: c => c.customerNumber == null ? '' : String(c.customerNumber), render: c => c.customerNumber },
    { field: 'name', label: t('customers.columns.name'), render: c => c.name },
    { field: 'email', label: t('customers.columns.email'), render: c => c.email },
    { field: 'phone', label: t('customers.columns.phone'), render: c => c.phone },
    { field: 'address', label: t('customers.columns.address'), render: c => c.address },
    { field: 'postalCode', label: t('customers.columns.postalCode'), render: c => c.postalCode },
    { field: 'city', label: t('customers.columns.city'), render: c => c.city },
    // The Beställare's banks (ClickUp 869eu6fpm).
    { field: 'banks', label: t('customers.columns.banks'), sortValue: c => bankNamesText(t, c.banks), filterValue: c => bankNamesText(t, c.banks), render: c => bankNamesText(t, c.banks) },
    { field: 'notes', label: t('customers.columns.notes'), render: c => c.notes },
    { field: 'createdAt', label: t('adminCustomers.columns.createdAt'), defaultSortDirection: 'desc', sortValue: c => (c.createdAt ? new Date(c.createdAt).getTime() : 0), filterValue: c => formatDateTime(c.createdAt), render: c => formatDateTime(c.createdAt) },
    { field: 'age', label: t('adminCustomers.columns.age'), filter: 'none', sortValue: c => calculateAgeDays(c.createdAt), render: c => calculateAgeDays(c.createdAt) },
    {
      field: 'verifiedCustomer',
      label: t('customers.columns.verified'),
      filter: 'select',
      selectAllLabel: t('customers.allStatuses'),
      selectOptions: statusOptions,
      sortValue: c => statusLabel(c.verifiedCustomer),
      render: c => statusBadge(c)
    }
    // The renderers only depend on the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [statusOptions, t]);

  // ?verifiedCustomer=, ?customerNumber= and ?companyNumber= pre-filter, as before.
  const initialFilters = useMemo<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const field of ['verifiedCustomer', 'customerNumber', 'companyNumber']) {
      const value = searchParams.get(field);
      if (value) init[field] = value;
    }
    return init;
  }, [searchParams]);

  // A standalone client has no client record at a contractor to open.
  const canOpen = (customer: ClientRow) => !customer.standalone;
  const openCustomer = (customer: ClientRow) => { if (canOpen(customer)) navigate(`/admin/customers/${customer.id}`); };
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  // The server exports every row that matches the list, not just this page.
  const exportToExcel = async () => {
    setExportError(false);
    setIsExporting(true);
    try {
      const fields = new Set(columns.map(column => column.field));
      const { blob, filename } = await adminApi.exportCustomers({
        ...lastQuery.current,
        columns: columnOrder.filter(field => fields.has(field))
      });
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('Failed to export clients:', error);
      setExportError(true);
    } finally {
      setIsExporting(false);
    }
  };

  const renderMobileCard = (customer: ClientRow, { open, selected }: { open: () => void; selected: boolean }) => (
    <div className={`card mb-2 admin-list-card ${selected ? 'border-primary' : ''}`}
      onClick={() => setSelectedCustomerId(previous => previous === customer.id ? null : customer.id)} onDoubleClick={open}>
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1 admin-list-card__body">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate">{customer.customerNumber != null && `#${customer.customerNumber} `}{customer.name}</span>
              {statusBadge(customer, 'flex-shrink-0')}
            </div>
            {(customer.companyName || customer.standalone) && (
              <div className="text-muted small">
                <i className="feather-briefcase me-1"></i>{contractorLink(customer)}{customer.companyNumber != null && ` (${customer.companyNumber})`}
              </div>
            )}
            {customer.email && <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{customer.email}</div>}
            {customer.phone && <div className="text-muted small"><i className="feather-phone me-1"></i>{customer.phone}</div>}
            {(customer.address || customer.city) && (
              <div className="text-muted small text-truncate">
                <i className="feather-map-pin me-1"></i>{[customer.address, customer.postalCode, customer.city].filter(Boolean).join(', ')}
              </div>
            )}
            <div className="d-flex flex-wrap gap-3 mt-1">
              <span className="text-muted small"><i className="feather-calendar me-1"></i>{formatDateTime(customer.createdAt)}</span>
              <span className="text-muted small"><i className="feather-clock me-1"></i>{calculateAgeDays(customer.createdAt)} {t('adminCustomers.columns.age')}</span>
            </div>
            {customer.notes && <div className="text-muted small text-truncate mt-1"><i className="feather-file-text me-1"></i>{customer.notes}</div>}
          </div>
          {canOpen(customer) && <div className="d-flex gap-1 flex-shrink-0 ms-2">
            <button
              type="button"
              className="btn btn-sm btn-light-primary admin-list-card__open"
              onClick={(e) => { e.stopPropagation(); open(); }}
              title={t('customers.viewCustomer')}
              aria-label={t('customers.viewCustomer')}
            >
              <i className="feather-arrow-right" aria-hidden="true"></i>
            </button>
          </div>}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminCustomers.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminCustomers.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminCustomers.description')}</p>
        </div>
      </div>

      {loadError && <div className="alert alert-danger" role="alert">{t('adminLists.loadError')}</div>}
      {exportError && <div className="alert alert-danger" role="alert">{t('adminLists.exportError')}</div>}

      <Card>
        <ProjectsListTable
          rows={customers}
          server={{ total, loading: isLoading, onQueryChange: loadPage }}
          columns={columns}
          rowKey={c => c.id}
          columnOrder={columnOrder}
          onColumnReorder={handleColumnReorder}
          reorderTitle={t('adminCustomers.dragToReorder')}
          initialSortField="customerNumber"
          initialSortDirection="asc"
          initialFilters={initialFilters}
          onOpen={openCustomer}
          canOpen={canOpen}
          onSelect={c => setSelectedCustomerId(previous => previous === c.id ? null : c.id)}
          selectedKey={selectedCustomerId}
          actionTitle={t('customers.viewCustomer')}
          emptyText={t('customers.noCustomers')}
          noResultsText={t('projects.noResults')}
          rangeText={range => t('adminCustomers.showingRange', range)}
          mobileFilter={{
            searchField: 'name',
            select: { field: 'verifiedCustomer', allLabel: t('customers.allStatuses'), options: statusOptions },
            sort: {
              label: t('projects.sort.label'),
              options: [
                { field: 'customerNumber', direction: 'asc', label: t('adminCustomers.sort.customerNumber') },
                { field: 'name', direction: 'asc', label: t('adminCustomers.sort.nameAZ') },
                { field: 'createdAt', direction: 'desc', label: t('adminCustomers.sort.newest') }
              ]
            }
          }}
          renderMobileCard={renderMobileCard}
          testIds={{ table: 'admin-customer-list-table', cards: 'admin-customer-list-cards' }}
          toolbar={
            <>
              <Button className="admin-list-toolbar__btn" onClick={() => navigate('/admin/customers/create')} aria-label={t('customerSetup.title')}>
                <i className="feather-plus" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customerSetup.title')}</span>
              </Button>
              <Button
                variant="light-brand"
                className="admin-list-toolbar__btn"
                onClick={() => selectedCustomer && openCustomer(selectedCustomer)}
                disabled={!selectedCustomer || !canOpen(selectedCustomer)}
                aria-label={t('customers.viewCustomer')}
              >
                <i className="feather-eye" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.viewCustomer')}</span>
              </Button>
              <Button variant="light-brand" className="admin-list-toolbar__btn" onClick={exportToExcel} disabled={isExporting} aria-label={t('customers.export')}>
                <i className="feather-download" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.export')}</span>
              </Button>
            </>
          }
        />
      </Card>
    </DashboardLayout>
  );
}
